import { useState } from "react";
import { X, Calendar, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: "music", label: "Music & Concerts" },
  { value: "sports", label: "Sports & Outdoor" },
  { value: "cultural", label: "Cultural & Religious" },
  { value: "tech", label: "Technology & Business" },
  { value: "lifestyle", label: "Lifestyle & Pop Culture" }
];

const states = [
  { value: "johor", label: "Johor" },
  { value: "kedah", label: "Kedah" },
  { value: "kelantan", label: "Kelantan" },
  { value: "kl", label: "Kuala Lumpur" },
  { value: "labuan", label: "Labuan" },
  { value: "melaka", label: "Melaka" },
  { value: "ns", label: "Negeri Sembilan" },
  { value: "pahang", label: "Pahang" },
  { value: "penang", label: "Pulau Pinang" },
  { value: "perak", label: "Perak" },
  { value: "perlis", label: "Perlis" },
  { value: "putrajaya", label: "Putrajaya" },
  { value: "sabah", label: "Sabah" },
  { value: "sarawak", label: "Sarawak" },
  { value: "selangor", label: "Selangor" },
  { value: "terengganu", label: "Terengganu" }
];

export default function SubmitEventModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    end_date: "",
    time: "",
    location: "",
    state: "",
    category: "",
    price: "",
    submitted_by: "",
    is_underground: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date || !formData.location || !formData.category || !formData.state) {
      toast.error("Sila isi semua field yang wajib");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/events/submit`, formData);
      toast.success("Event berjaya dihantar! Menunggu kelulusan admin.");
      setFormData({
        title: "", date: "", end_date: "", time: "",
        location: "", state: "", category: "", price: "", submitted_by: "", is_underground: false
      });
      onClose();
    } catch (error) {
      toast.error("Gagal menghantar event. Sila cuba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto glass-card rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-card/95 backdrop-blur-sm p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-foreground">Submit Event</h2>
                  <p className="text-xs text-muted-foreground">Kongsikan event yang anda tahu</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Nama Event <span className="text-pink-500">*</span>
                </label>
                <Input
                  placeholder="cth: Coldplay Concert Malaysia"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  className="bg-white/5 border-white/10 text-foreground"
                  data-testid="submit-title"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Kategori <span className="text-pink-500">*</span>
                </label>
                <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-foreground" data-testid="submit-category">
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Tarikh Mula <span className="text-pink-500">*</span>
                  </label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange("date", e.target.value)}
                    className="bg-white/5 border-white/10 text-foreground"
                    data-testid="submit-date"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Tarikh Tamat
                  </label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleChange("end_date", e.target.value)}
                    className="bg-white/5 border-white/10 text-foreground"
                  />
                </div>
              </div>

              {/* Time */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Masa</label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange("time", e.target.value)}
                  className="bg-white/5 border-white/10 text-foreground"
                />
              </div>

              {/* Location & State */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Lokasi <span className="text-pink-500">*</span>
                  </label>
                  <Input
                    placeholder="cth: Axiata Arena"
                    value={formData.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    className="bg-white/5 border-white/10 text-foreground"
                    data-testid="submit-location"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Negeri <span className="text-pink-500">*</span>
                  </label>
                  <Select value={formData.state} onValueChange={(v) => handleChange("state", v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-foreground" data-testid="submit-state">
                      <SelectValue placeholder="Pilih negeri" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10 max-h-[200px]">
                      {states.map(state => (
                        <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Price & Name */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Harga</label>
                  <Input
                    placeholder="cth: RM199 atau Free"
                    value={formData.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    className="bg-white/5 border-white/10 text-foreground"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Nama Anda</label>
                  <Input
                    placeholder="cth: Ali (optional)"
                    value={formData.submitted_by}
                    onChange={(e) => handleChange("submitted_by", e.target.value)}
                    className="bg-white/5 border-white/10 text-foreground"
                  />
                </div>
              </div>

              {/* Underground Event Checkbox */}
              <div className="flex items-center gap-3 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                <input
                  type="checkbox"
                  id="is_underground"
                  checked={formData.is_underground}
                  onChange={(e) => handleChange("is_underground", e.target.checked)}
                  className="w-5 h-5 rounded border-orange-500/50 text-orange-500 focus:ring-orange-500/50 bg-white/5"
                  data-testid="submit-underground"
                />
                <label htmlFor="is_underground" className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <span className="text-xl">🔥</span>
                  <span>
                    <strong>Underground Event</strong>
                    <span className="text-muted-foreground ml-1">- Event kecil/local yang tak famous tapi best!</span>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-xl h-12 font-semibold"
                data-testid="submit-event-btn"
              >
                {loading ? "Menghantar..." : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Hantar Event
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Event akan disemak oleh admin sebelum dipaparkan.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
